// Realpad vracia pre etapu boryNoveBloky počet izieb s príponou KK (napr. "2KK", "1,5KK-A"),
// ostatné etapy vracajú čisté číslo ("2", "1,5"). "kk" = kuchynský kút, čiže je to údaj
// pre kupujúceho - preto sa príponou zbavujeme len pri porovnávaní (nadpis, cenové
// pravidlá), nikdy nie pri zobrazovaní. Jedinou výnimkou je zoznam bytov, kde mutácia
// retrievedFlats normalizuje dáta natvrdo, lebo z nich vychádzajú tlačidlá filtra.
const ROOMS_SUFFIX = /KK(-A)?$/i

// Nadpisy podľa počtu izieb. Hodnoty sú slovenské kľúče pre $t().
// "1,5" tu zámerne nie je - každý komponent ho zobrazuje po svojom.
const ROOM_TITLES: { [rooms: string]: string } = {
  '1': 'Jednoizbový byt',
  '2': 'Dvojizbový byt',
  '3': 'Trojizbový byt',
  '4': 'Štvorizbový byt',
  '5': 'Päťizbový byt',
  '6': 'Šesťizbový byt',
  '7': 'Sedemizbový byt',
  '8': 'Osemizbový byt',
  '9': 'Deväťizbový byt'
}

interface Flat {
  // eslint-disable-next-line @typescript-eslint/camelcase
  flat_number_of_rooms?: string;
  // eslint-disable-next-line @typescript-eslint/camelcase
  flat_disposition?: string;
  // eslint-disable-next-line @typescript-eslint/camelcase
  flat_penta_internal_id?: string;
  building?: {
    // eslint-disable-next-line @typescript-eslint/camelcase
    building_stage?: string;
  };
}

export function normalizeNumberOfRooms(rooms?: string): string | undefined {
  if (!rooms) {
    return rooms
  }

  return String(rooms).replace(ROOMS_SUFFIX, '')
}

// Prepíše flat_number_of_rooms priamo v objekte bytu a byt vráti.
// Len pre zoznam bytov (retrievedFlats) - inde sa pôvodná hodnota musí zachovať.
export function normalizeFlat<T extends Flat>(flat: T): T {
  if (flat && flat.flat_number_of_rooms) {
    // eslint-disable-next-line @typescript-eslint/camelcase
    flat.flat_number_of_rooms = normalizeNumberOfRooms(flat.flat_number_of_rooms)
  }

  return flat
}

// Kľúč nadpisu pre $t(), null ak počet izieb nevieme priradiť.
export function roomsTitle(flat: Flat): string | null {
  if (!flat) {
    return null
  }

  const rooms = normalizeNumberOfRooms(flat.flat_number_of_rooms)

  return (rooms && ROOM_TITLES[rooms])
    || (flat.flat_disposition && ROOM_TITLES[flat.flat_disposition])
    || null
}

// Jeden a pol izbový byt - nemá nadpis v ROOM_TITLES, každý komponent ho zobrazuje po svojom.
export function isOneAndHalfRoom(flat: Flat): boolean {
  if (!flat) {
    return false
  }

  return normalizeNumberOfRooms(flat.flat_number_of_rooms) === '1,5'
    || flat.flat_disposition === '1,5'
}

// Byty s cenou na vyžiadanie: terasový byt B.06.A v Nádvorí (bory5) a 5-izbové
// byty v Nových Blokoch. B.06.A je obchodné rozhodnutie - terasové byty na 6./7. NP
// sa cenia individuálne. Pôvodne boli takéto byty dva, F.07.A pribudol v 12/2025
// a bol odstránený v 02/2026.
//
// POZOR na kľúč: porovnávame flat_penta_internal_id, nie flat_internal_id.
// Pre bory5 sú obe polia rovnaké (SalesForce ich mapuje z Flat_Name), ale pre etapy
// z Realpad XML je flat_internal_id v tvare "B2.01.A_1528" - tam by sa porovnanie
// s 'B.06.A' nikdy netrafilo. Penta id funguje v oboch prípadoch.
const PRICE_ON_REQUEST_FLAT = 'B.06.A'
const PRICE_ON_REQUEST_STAGE = 'boryNoveBloky'
const PRICE_ON_REQUEST_ROOMS = '5'

// Má sa namiesto ceny zobraziť "Cena na vyžiadanie"?
// Normalizuje si počet izieb sám, takže funguje aj nad surovými dátami z API
// (detail bytu, porovnávacie okno, Podlažie), nielen nad zoznamom zo store.
export function isPriceOnRequest(flat?: Flat): boolean {
  if (!flat) {
    return false
  }

  if (flat.flat_penta_internal_id === PRICE_ON_REQUEST_FLAT) {
    return true
  }

  // V porovnávacej lište chýba building - vtedy sa pravidlo etapy nedá vyhodnotiť.
  const stage = flat.building ? flat.building.building_stage : undefined
  if (stage !== PRICE_ON_REQUEST_STAGE) {
    return false
  }

  return normalizeNumberOfRooms(flat.flat_number_of_rooms) === PRICE_ON_REQUEST_ROOMS
    || normalizeNumberOfRooms(flat.flat_disposition) === PRICE_ON_REQUEST_ROOMS
}
